// "Which-key" overlay: while a chord is pending (you pressed G, or D on the
// Trade page), a floating panel rises above the footer listing every next key
// you can press and what it does. Mount once inside <KeyboardProvider>.
import { SHORTCUTS, formatChord, formatToken } from "keyboard/shortcuts";
import { useKeyboardBuffer, useKeyboardContext } from "keyboard/useKeyboard";
import type { CSSProperties } from "react";
import {
  BORDER_COLORS,
  COLORS,
  FIELD_COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";

export function ChordOverlay() {
  const { manager } = useKeyboardContext();
  const buffer = useKeyboardBuffer();

  if (!buffer.length) return null;

  // Every wired, in-scope binding that extends the pending chord.
  const continuations = SHORTCUTS.filter(
    (s) =>
      !s.hidden &&
      s.keys.length > buffer.length &&
      buffer.every((tok, i) => s.keys[i] === tok) &&
      manager.isScopeActive(s.scope) &&
      manager.hasHandler(s.id),
  );
  if (!continuations.length) return null;

  return (
    <div style={wrap} role="dialog" aria-label="Keyboard chord options">
      <div style={card}>
        <div style={header}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <kbd style={kbd}>{formatChord(buffer)}</kbd>
            <span style={{ color: TEXT_COLORS.three }}>then press…</span>
          </span>
          <span style={{ color: TEXT_COLORS.three, fontSize: 11 }}>Esc to cancel</span>
        </div>
        <div style={grid}>
          {continuations.map((s) => (
            <div key={s.id} style={row}>
              <kbd style={kbd}>{formatToken(s.keys[buffer.length])}</kbd>
              <span style={{ color: TEXT_COLORS.two, fontSize: 13 }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const wrap: CSSProperties = {
  position: "fixed",
  bottom: 52,
  left: 0,
  right: 0,
  zIndex: 2147482000,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  padding: "0 16px",
};

const card: CSSProperties = {
  pointerEvents: "auto",
  width: "100%",
  maxWidth: 560,
  background: LAYER_COLORS.one,
  border: `1px solid ${BORDER_COLORS.three}`,
  borderRadius: 12,
  // Elevation scrim — no black-alpha shadow token yet (kept raw intentionally).
  boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
  padding: 14,
};

const header: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 10,
  fontSize: 12,
};

const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
  gap: "6px 14px",
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
};

const kbd: CSSProperties = {
  display: "inline-flex",
  minWidth: 20,
  justifyContent: "center",
  border: `1px solid ${BORDER_COLORS.three}`,
  background: FIELD_COLORS.one,
  borderRadius: 4,
  padding: "1px 6px",
  fontFamily: "monospace",
  fontSize: 12,
  color: COLORS.highlight.one,
  flexShrink: 0,
};
